<script setup>
/**
 * CarePackageLanding — what the recipient of a care package sees.
 *
 * A parcel, opened: the illustration on top, the sender's note tucked over
 * its edge, then the cards laid out as they were drawn, the whole reading (it
 * was paid for, for them), the Mystic's close and ONE door: Dive in deeper.
 * Tapping the door is the sign-up: the invite already holds their name and
 * contact, and the link was delivered to that contact, so nothing is asked
 * but the terms, in one line. The thank-you already happened the moment the
 * page opened.
 *
 * The server says who is looking (gift.viewer_role). The sender gets his own
 * page, the letter (SentGift.vue, 2026-10-19); another member sees that it
 * was made for someone else, no door; the recipient and a stranger get the
 * door. A revoked link lands here too: no reading, the same door.
 * Spec: https://claude.ai/artifact/JgiptzEg41x3wAVTS5Gfjr
 */
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { careDoor, careSignin } from '@/api-service'
import { store } from '@/store'
import SentGift from '@/components/ui/SentGift.vue'
import PictureOpen from '@/components/ui/share/PictureOpen.vue'
import { forgetBrowserAccount } from '@/utils/sender-device'

const props = defineProps({
  data: { type: Object, required: true },   // reading-preview payload with .gift
  inv: { type: String, required: true },    // the invite token
  // Sent here from the gift's room by a chip she tapped: the address already
  // has an account, so go straight through the door to its code step.
  autoDoor: { type: Boolean, default: false },
})
const emit = defineEmits(['entered', 'login', 'leave', 'switch-account'])

const gift = computed(() => props.data.gift || {})
const revoked = computed(() => !!gift.value.revoked)
const cards = computed(() => (props.data.cards || []))
const role = computed(() => gift.value.viewer_role || 'anonymous')
const isSender = computed(() => role.value === 'sender')
const isOtherMember = computed(() => role.value === 'member')
// The sender's own browser, signed out (R4.35, 2026-10-21): his view, never
// an open, and no door (the door would take the link).
const isSenderDevice = computed(() => role.value === 'sender_device')
const hasDoor = computed(() => !isSender.value && !isOtherMember.value && !isSenderDevice.value)
function notTheSender() {
  forgetBrowserAccount()
  try { window.location.reload() } catch (e) { /* */ }
}
const signedIn = computed(() => !!store.querent_id || role.value === 'recipient')
const forName = computed(() => gift.value.for_name || 'you')
const fromName = computed(() => gift.value.from_name || 'someone who cares about you')
const busy = ref(false)
const err = ref('')
// Someone who already has a place here: the server mails a code to the address
// on their account and we finish the sign-in right here, on this page. No
// password screen, no leaving the reading someone made for them.
const sentTo = ref('')
const code = ref('')
// A copied link holds no contact (gift.door === 'email'): the opener gives
// their email, a code goes there, and the code makes their place here.
const emailDoor = computed(() => gift.value.door === 'email' && !signedIn.value)
const email = ref('')
const emailOk = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()))
const newAccount = ref(false)

// 'readingPast' / 'past' / 'outer_influence' -> 'Past' / 'Past' / 'Outer Influence'
function position(c) {
  const r = ((c && c.reference) || '').replace(/^reading(?=[A-Z_])/, '')
  return r ? r.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (m) => m.toUpperCase()).trim() : ''
}

const closeLine = computed(() => {
  if (isOtherMember.value) return `This one was made for ${forName.value}. When it is your turn, you will know.`
  if (isSenderDevice.value) return `This is what you sent ${forName.value}. Sign in to see where it is.`
  return gift.value.close || `${gift.value.for_name || 'Friend'}, someone lit a candle for you today. If you would like to see it from where you stand, come in.`
})

function doorFailed(e) {
  const said = e && e.response && e.response.data && e.response.data.error
  err.value = said ? `${said}.` : 'The door did not open. Try again.'
}

async function enter() {
  if (busy.value) return
  busy.value = true; err.value = ''
  if (emailDoor.value && !emailOk.value) { err.value = 'Your email opens it.'; busy.value = false; return }
  try {
    const r = emailDoor.value ? await careDoor(props.inv, { email: email.value.trim() }) : await careDoor(props.inv)
    if (r && emailDoor.value && r.signin === 'code') {
      newAccount.value = !r.existing
      sentTo.value = r.sent_to || email.value.trim()
      return
    }
    if (r && r.existing) {
      // An older server has no code to send: fall back to the login screen.
      // 'code' means a code and a link went out; anything else ('unavailable',
      // or an older backend with no field) falls back to the ordinary sign-in.
      if (r.signin !== 'code') { emit('login', { email: r.email || '' }); return }
      sentTo.value = r.sent_to || r.email || 'the address on your account'
      return
    }
    if (r && (r.token || r.member)) { emit('entered', r); return }
    err.value = 'The door did not open. Try again.'
  } catch (e) { doorFailed(e) } finally { busy.value = false }
}

onMounted(() => {
  if (props.autoDoor) enter()
})
// The code step stands where the door was, at the foot of the whole letter:
// bring it into view and put the cursor in it, so she never has to hunt for it
// (gift bug walk 2026-10-25 #18: it sat about 1700px down, unseen).
const codeStep = ref(null)
const codeInput = ref(null)
watch(sentTo, (to) => {
  if (!to) return
  nextTick(() => {
    const el = codeStep.value
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (codeInput.value && codeInput.value.focus) codeInput.value.focus({ preventScroll: true })
  })
})

const SIGNIN_TROUBLE = {
  bad_code: 'That code did not match. Try again.',
  expired: 'That code expired. Tap Send it again.',
}
async function signin() {
  if (busy.value) return
  busy.value = true; err.value = ''
  try {
    const r = await careSignin(props.inv, { code: code.value.trim(), ...(newAccount.value ? { newAccount: true } : {}) })
    if (r && r.token) { emit('entered', r); return }
    err.value = 'That did not go through. Try again.'
  } catch (e) {
    const reason = e && e.response && e.response.data && e.response.data.reason
    err.value = SIGNIN_TROUBLE[reason] || 'That did not go through. Try again.'
  } finally { busy.value = false }
}
</script>

<template>
  <SentGift v-if="isSender" :data="data" @leave="(sub) => emit('leave', sub)" />
  <section v-else class="cpl" aria-label="A care package">
    <!-- The sender and another member have somewhere to go back to; the
         recipient's only way on is the door, so no X for them. -->
    <button v-if="!hasDoor" type="button" class="cpl-x" aria-label="Close" data-testid="care-close"
            @click="emit('leave', data.reader_subdomain)"><i class="fas fa-times" aria-hidden="true"></i></button>
    <!-- The picture opens (picture-caption rev 1, 2026-10-30): no caption on
         the front door; tapped, it fills the screen with the caption over its
         foot. The expand mark sits top-right, clear of the eyebrow and of the
         sheet over the picture's bottom edge (lower when the X is there). -->
    <div v-if="!revoked && data.image_url" class="cpl-hero" :class="{ 'has-x': !hasDoor }">
      <PictureOpen :src="data.image_url" :caption="data.image_caption || ''"><span class="cpl-hero-shade" aria-hidden="true"></span></PictureOpen>
      <span class="cpl-eyebrow cpl-eyebrow-hero">A care package</span>
    </div>

    <div class="cpl-sheet" :class="{ 'no-hero': revoked || !data.image_url }">
      <header class="cpl-note-card">
        <span v-if="revoked || !data.image_url" class="cpl-eyebrow">A care package</span>

        <template v-if="isOtherMember || isSenderDevice">
          <h1 class="cpl-for">Made for {{ forName }}</h1>
          <p class="cpl-from">from {{ fromName }}</p>
          <!-- Most often it IS them, on a browser signed in as someone else.
               Name the inbox (masked) and give them the way to their own. -->
          <p v-if="gift.sent_to" class="cpl-sent-to">
            Sent to {{ gift.sent_to }}. Is that you?
            <button type="button" class="cpl-switch" @click="emit('switch-account')">Switch account</button>
          </p>
        </template>
        <template v-else>
          <h1 class="cpl-for">{{ forName }},</h1>
          <p class="cpl-from">From {{ fromName }}</p>
        </template>

        <p v-if="!revoked && gift.note" class="cpl-note" style="white-space: pre-line">“{{ gift.note }}”</p>
        <p v-if="revoked" class="cpl-revoked">{{ gift.from_name || 'They' }} took this reading back. Sometimes the moment passes. The door is still open, though.</p>
      </header>

      <div v-if="!revoked" class="cpl-reading">
        <ul v-if="cards.length" class="cpl-spread" :style="{ '--n': cards.length }">
          <li v-for="(c, i) in cards" :key="'s' + i">
            <img v-if="c.image_url" :src="c.image_url" :alt="c.name" :class="{ reversed: c.reversed }" />
            <span v-if="position(c)" class="cpl-pos">{{ position(c) }}</span>
          </li>
        </ul>

        <h2 v-if="data.summary_title" class="cpl-title">{{ data.summary_title }}</h2>
        <p v-if="data.question" class="cpl-question">{{ fromName }} asked: {{ data.question }}</p>

        <ol v-if="cards.length" class="cpl-cards">
          <li v-for="(c, i) in cards" :key="i" class="cpl-card">
            <h3><span v-if="position(c)" class="cpl-card-pos">{{ position(c) }} · </span>{{ c.name }}<small v-if="c.reversed"> reversed</small></h3>
            <p>{{ c.reading_text || c.lens_meaning || c.card_meaning }}</p>
          </li>
        </ol>
        <p v-if="data.reading_summary" class="cpl-summary">{{ data.reading_summary }}</p>
      </div>

      <footer class="cpl-close">
        <p class="cpl-close-line" data-testid="care-close-line">{{ closeLine }}</p>

        <template v-if="hasDoor">
          <!-- Already a member: the code step takes the door's place, in line,
               so they never leave the page the package was sent to. -->
          <div v-if="sentTo" ref="codeStep" class="cpl-code-step" data-testid="care-code-step">
            <p class="cpl-code-said">We sent a six-digit code and a sign-in link to <b>{{ sentTo }}</b>.</p>
            <input
              ref="codeInput"
              class="cpl-code" v-model="code" type="text" inputmode="numeric" autocomplete="one-time-code"
              maxlength="6" aria-label="Six-digit code" placeholder="000000"
              @keydown.enter.prevent="signin"
            />
            <button type="button" class="cpl-continue" :disabled="busy" @click="signin">
              {{ busy ? 'One moment…' : 'Continue' }}
            </button>
            <p v-if="err" class="cpl-err" role="alert">{{ err }}</p>
            <button type="button" class="cpl-again" :disabled="busy" @click="enter">Send it again</button>
          </div>
          <template v-else>
            <label v-if="emailDoor" class="cpl-email-wrap">
              <span>Your email</span>
              <input class="cpl-email" v-model="email" type="email" inputmode="email" autocomplete="email"
                     placeholder="you@example.com" aria-label="Your email" @keydown.enter.prevent="enter" />
            </label>
            <button type="button" class="cpl-door" :disabled="busy" @click="enter">
              {{ busy ? 'Opening…' : 'Dive in deeper' }}
            </button>
            <p v-if="err" class="cpl-err" role="alert">{{ err }}</p>
            <p v-if="!signedIn" class="cpl-terms">
              <template v-if="emailDoor">We'll send a code to that address; it makes a place for you here as {{ forName }}.</template>
              <template v-else>This makes a place for you here as {{ forName }}.</template>
              By diving in you agree to the
              <a href="https://mysticaida.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a>
              and
              <a href="https://mysticaida.ai/acceptable-use" target="_blank" rel="noopener noreferrer">Acceptable Use</a>.
            </p>
          </template>
        </template>
        <template v-else-if="isSenderDevice">
          <button type="button" class="cpl-door" data-testid="care-sender-signin" @click="emit('login', {})">Sign in</button>
          <button type="button" class="cpl-again" data-testid="care-not-sender" @click="notTheSender">Not {{ gift.from_name || 'them' }}? Open it here.</button>
        </template>
        <template v-else>
        <button type="button" class="cpl-leave" @click="emit('leave', data.reader_subdomain)">
          Back to {{ data.reader_name ? `${data.reader_name}'s room` : 'the room' }}
        </button>
        </template>

        <p v-if="data.reader_name" class="cpl-mystic">
          <img v-if="data.reader_avatar" :src="data.reader_avatar" alt="" />
          <span>read by <b>{{ data.reader_name }}</b></span>
        </p>
      </footer>
    </div>
  </section>
</template>

<style scoped>
.cpl { position: relative; max-width: 680px; margin: 0 auto; padding: 0 0 32px; color: #2a1845; animation: cpl-in 600ms ease-out both; }
/* The same round X as the share screen's (.sp-close). */
.cpl-x { position: absolute; top: 8px; right: 16px; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: #f3e8ff; color: #4a236b; border: none; cursor: pointer; z-index: 3; font-size: 15px; line-height: 1; box-shadow: 0 4px 14px -6px rgba(0,0,0,0.5); }
@keyframes cpl-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .cpl { animation: none; } }

/* The parcel's lid: the illustration, with the note tucked over its edge. */
.cpl-hero { position: relative; margin: 0 8px; border-radius: 18px; overflow: hidden; aspect-ratio: 3 / 2; box-shadow: 0 24px 50px -24px rgba(0,0,0,0.8); }
.cpl-hero img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cpl-hero.has-x :deep(.po-mark) { top: 62px; }
.cpl-hero-shade { position: absolute; inset: 0; z-index: 1; pointer-events: none; background: linear-gradient(180deg, rgba(14,7,25,0.35) 0%, rgba(14,7,25,0) 30%, rgba(14,7,25,0) 60%, rgba(14,7,25,0.55) 100%); }
.cpl-eyebrow { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; color: #8a6a2a; }
.cpl-eyebrow-hero { position: absolute; top: 16px; left: 18px; z-index: 1; pointer-events: none; color: #FFDFA3; text-shadow: 0 1px 8px rgba(0,0,0,0.6); }

.cpl-sheet { position: relative; margin: -56px 0 0; background: #f3ebdc; border-radius: 18px; overflow: hidden; box-shadow: 0 30px 60px -30px rgba(0,0,0,0.9); }
.cpl-sheet.no-hero { margin-top: 0; }

.cpl-note-card { padding: 26px 26px 20px; display: flex; flex-direction: column; gap: 6px; background: linear-gradient(180deg, #faf5ea 0%, #f3ebdc 100%); border-bottom: 1px solid rgba(201,169,78,0.35); }
.cpl-for { font-family: 'Cinzel', serif; font-weight: 600; font-size: 30px; line-height: 1.1; margin: 0; color: #2a1845; text-wrap: balance; }
.cpl-from { margin: 0; font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: #8a6a2a; }
.cpl-sent-to { margin: 10px 0 0; font-size: 13px; color: #6b5636; }
.cpl-switch { margin-left: 6px; background: none; border: none; padding: 4px 2px; min-height: 32px;
  color: #8a5a12; font: inherit; font-weight: 700; text-decoration: underline; cursor: pointer; }
.cpl-note { margin: 12px 0 0; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 21px; line-height: 1.45; color: #2a1845; text-wrap: pretty; }
.cpl-revoked { margin: 12px 0 0; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 18px; line-height: 1.45; color: #5a4a72; }

.cpl-reading { padding: 22px 26px 26px; }
/* The spread as it lay on the table: every card, side by side, its position under it. */
.cpl-spread { list-style: none; margin: 0 0 22px; padding: 0; display: grid; grid-template-columns: repeat(var(--n, 4), minmax(0, 1fr)); gap: 12px; }
.cpl-pos { overflow-wrap: anywhere; }
.cpl-spread li { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.cpl-spread img { width: 100%; max-width: 132px; aspect-ratio: 2 / 3; object-fit: cover; border-radius: 10px; border: 1px solid #c9a94e; box-shadow: 0 10px 22px -12px rgba(42,24,69,0.6); background: #2a1845; }
.cpl-spread img.reversed { transform: rotate(180deg); }
.cpl-pos { font-family: 'Cinzel', serif; font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; color: #8a6a2a; text-align: center; }

.cpl-title { font-family: 'Cinzel', serif; font-weight: 600; font-size: 22px; text-align: center; margin: 0 0 6px; text-wrap: balance; }
.cpl-question { margin: 0 0 20px; text-align: center; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 16.5px; color: #6b5a82; }

.cpl-cards { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 16px; }
.cpl-card h3 { font-family: 'Cinzel', serif; font-weight: 600; font-size: 14px; margin: 0 0 4px; color: #2a1845; }
.cpl-card-pos { color: #8a6a2a; font-weight: 500; letter-spacing: 0.06em; }
.cpl-card h3 small { font-family: inherit; font-size: 11px; color: #6b5a82; letter-spacing: 0.06em; margin-left: 6px; }
.cpl-card p { margin: 0; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17.5px; line-height: 1.5; color: #3d2f55; text-wrap: pretty; }
.cpl-summary { margin: 22px 0 0; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 18px; line-height: 1.55; color: #2a1845; border-left: 2px solid #c9a94e; padding-left: 14px; text-wrap: pretty; }

.cpl-close { background: radial-gradient(125% 95% at 50% 0%, #1c1035 0%, #120827 54%, #0d0520 100%); color: #e9dcff; padding: 26px 26px 22px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 14px; }
.cpl-close-line { margin: 0 auto; max-width: 46ch; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 20px; line-height: 1.5; text-wrap: balance; }
.cpl-door { display: inline-flex; align-items: center; gap: 9px; padding: 15px 30px; border-radius: 999px; border: 1px solid rgba(240,217,163,0.55); background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #FFDFA3; font-weight: 700; font-size: 13.5px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; box-shadow: 0 12px 30px -8px rgba(78,39,118,0.7); }
.cpl-door:disabled { opacity: 0.6; cursor: default; }
.cpl-email-wrap { display: flex; flex-direction: column; align-items: center; gap: 6px; margin: 0 auto 14px; max-width: 320px; font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(255,223,163,0.8); }
.cpl-email { width: 100%; box-sizing: border-box; min-height: 44px; padding: 10px 14px; border-radius: 12px; border: 1px solid rgba(240,217,163,0.45); background: rgba(255,255,255,0.06); color: #fff; font-size: 16px; text-align: center; text-transform: none; letter-spacing: 0; }
.cpl-email:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
.cpl-door:focus-visible, .cpl-leave:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
.cpl-leave { background: none; border: 1px solid rgba(233,220,255,0.35); color: #e9dcff; border-radius: 999px; min-height: 44px; padding: 11px 22px; font-size: 13px; letter-spacing: 0.06em; cursor: pointer; }
.cpl-leave:hover { border-color: #FFDFA3; color: #FFDFA3; }
.cpl-err { margin: 0; color: #ffb4a0; font-size: 14px; }
/* Consent is a sentence, at a size a phone can read, and the two links are
   tappable rather than 11px targets between middots. */
.cpl-terms { margin: 0; max-width: 42ch; font-size: 13px; color: #a795c4; line-height: 1.6; }
.cpl-terms a { color: #c3a9ff; display: inline-block; padding: 8px 4px; margin: -8px -4px; text-decoration: underline; text-underline-offset: 3px; }
.cpl-terms a:hover { color: #FFDFA3; }

/* The code step, where the door stood. */
.cpl-code-step { display: flex; flex-direction: column; align-items: center; gap: 12px; width: 100%; }
.cpl-code-said { margin: 0; max-width: 40ch; font-size: 14.5px; line-height: 1.5; color: #d8c8ff; }
/* The masked address moves to the next line whole; the ellipsis in it is
   otherwise a break opportunity and it splits mid-address. */
.cpl-code-said b { font-weight: 600; color: #FFDFA3; white-space: nowrap; }
.cpl-code { width: 100%; max-width: 220px; box-sizing: border-box; min-height: 44px; padding: 10px 14px; border-radius: 12px;
  border: 1px solid rgba(240,217,163,0.4); background: rgba(255,255,255,0.05); color: #FFDFA3;
  font-family: 'Cinzel', serif; font-size: 20px; letter-spacing: 0.34em; text-align: center; outline: none; }
.cpl-code::placeholder { color: rgba(233,220,255,0.28); letter-spacing: 0.34em; }
.cpl-code:focus { border-color: #FFDFA3; box-shadow: 0 0 0 3px rgba(240,217,163,0.14); }
.cpl-continue { min-height: 44px; padding: 12px 30px; border-radius: 999px; border: 1px solid rgba(240,217,163,0.55);
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #FFDFA3; font-weight: 700; font-size: 13.5px;
  letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; }
.cpl-continue:disabled { opacity: 0.6; cursor: default; }
.cpl-again { min-height: 44px; padding: 11px 12px; border: none; background: none; color: #c3a9ff; font-family: inherit;
  font-size: 13px; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.cpl-again:hover { color: #FFDFA3; }
.cpl-continue:focus-visible, .cpl-again:focus-visible, .cpl-code:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
.cpl-mystic { margin: 6px 0 0; display: inline-flex; align-items: center; gap: 10px; font-size: 12.5px; color: #8a78ad; }
.cpl-mystic img { width: 28px; height: 28px; border-radius: 6px; object-fit: cover; border: 1px solid rgba(240,217,163,0.4); }
.cpl-mystic b { color: #e9dcff; font-weight: 600; }

@media (max-width: 480px) {
  .cpl-hero { margin: 0; border-radius: 14px; }
  .cpl-sheet { margin-top: -40px; border-radius: 14px; }
  .cpl-note-card, .cpl-reading, .cpl-close { padding-left: 18px; padding-right: 18px; }
  .cpl-for { font-size: 24px; }
  .cpl-note { font-size: 19px; }
  .cpl-spread img { border-radius: 8px; }
}
/* Ten cards in one row is a 30px sliver. Let the spread wrap instead. */
@media (max-width: 560px) {
  .cpl-spread { grid-template-columns: repeat(auto-fill, minmax(70px, 1fr)); gap: 8px; }
}
</style>
